import { fetchReviewSummary } from '@theme/review-api';

/**
 * Custom element that displays review distribution chart
 */
class ReviewDistribution extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    const productId = this.dataset.productId;
    if (productId) {
      this.loadReviewDistribution(productId);
    }
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  /**
   * Loads and renders the review distribution
   * @param {string} productId
   */
  async loadReviewDistribution(productId) {
    if (!productId || productId === 'PREVIEW-ID') return;

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const loadingIndicator = this.querySelector('[ref="loadingIndicator"]');
    const distributionContent = this.querySelector('[ref="distributionContent"]');
    const distributionBars = this.querySelector('[ref="distributionBars"]');

    if (loadingIndicator) {
      loadingIndicator.classList.remove('visually-hidden');
    }
    if (distributionContent) {
      distributionContent.classList.add('visually-hidden');
    }

    try {
      const summary = await fetchReviewSummary(productId);

      if (this.#abortController.signal.aborted) return;

      if (loadingIndicator) {
        loadingIndicator.classList.add('visually-hidden');
      }

      if (!summary || summary.totalReviews === 0 || !summary.ratingDistribution) {
        this.classList.add('visually-hidden');
        return;
      }

      this.classList.remove('visually-hidden');
      this.renderDistribution(summary);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error loading review distribution:', error);
      }
      if (loadingIndicator) {
        loadingIndicator.classList.add('visually-hidden');
      }
    }
  }

  /**
   * Renders the distribution chart
   * @param {Object} summary
   * @param {number} summary.totalReviews
   * @param {Object} summary.ratingDistribution
   */
  renderDistribution(summary) {
    const { totalReviews, ratingDistribution } = summary;
    const distributionBars = this.querySelector('[ref="distributionBars"]');
    const distributionContent = this.querySelector('[ref="distributionContent"]');

    if (!distributionBars) return;

    let barsHTML = '';

    // Render bars from 5 stars down to 1 star
    for (let stars = 5; stars >= 1; stars--) {
      const count = ratingDistribution[stars] || 0;
      const percentage = totalReviews > 0 ? (count / totalReviews) * 100 : 0;

      barsHTML += `
        <div class="review-distribution-row">
          <div class="review-distribution-stars">
            ${stars} ${stars === 1 ? 'star' : 'stars'}
          </div>
          <div class="review-distribution-bar-container">
            <div class="review-distribution-bar" style="width: ${percentage}%"></div>
          </div>
          <div class="review-distribution-count">${count}</div>
        </div>
      `;
    }

    distributionBars.innerHTML = barsHTML;

    if (distributionContent) {
      distributionContent.classList.remove('visually-hidden');
    }
  }
}

if (!customElements.get('review-distribution')) {
  customElements.define('review-distribution', ReviewDistribution);
}
